/**
 * useApi Composable - Laumeris
 * Gestion centralisée des appels API
 */

import { ref, computed } from 'vue'

// Types
interface ApiError {
  message: string
  status?: number
  errors?: Record<string, string[]>
}

interface ApiResponse<T> {
  data: T | null
  error: ApiError | null
  success: boolean
}

interface RequestOptions extends RequestInit {
  auth?: boolean
  params?: Record<string, string | number | boolean | undefined>
}

// Base URL de l'API (à configurer selon environnement)
// En production, utiliser l'URL complète du Worker API avec le préfixe /api
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.laumeris.com/api'

// Token management
const getAccessToken = (): string | null => {
  return localStorage.getItem('accessToken')
}

const getRefreshToken = (): string | null => {
  return localStorage.getItem('refreshToken')
}

const setTokens = (accessToken: string, refreshToken: string): void => {
  localStorage.setItem('accessToken', accessToken)
  localStorage.setItem('refreshToken', refreshToken)
}

const clearTokens = (): void => {
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
}

// Refresh token logic
let isRefreshing = false
let refreshSubscribers: ((token: string) => void)[] = []

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token))
  refreshSubscribers = []
}

const addRefreshSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback)
}

const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return null

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ refreshToken })
    })

    if (!response.ok) {
      clearTokens()
      return null
    }

    const data = await response.json()
    setTokens(data.accessToken, data.refreshToken)
    return data.accessToken
  } catch {
    clearTokens()
    return null
  }
}

// Main API function
export function useApi() {
  const loading = ref(false)
  const error = ref<ApiError | null>(null)

  const buildUrl = (endpoint: string, params?: Record<string, string | number | boolean | undefined>): string => {
    const url = new URL(`${API_BASE_URL}${endpoint}`, window.location.origin)

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value))
        }
      })
    }

    return url.toString()
  }

  const request = async <T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> => {
    const { auth = true, params, ...fetchOptions } = options

    loading.value = true
    error.value = null

    try {
      // Build headers
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(fetchOptions.headers as Record<string, string> || {})
      }

      // Add auth token if required
      if (auth) {
        const token = getAccessToken()
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }
      }

      // Make request
      let response = await fetch(buildUrl(endpoint, params), {
        ...fetchOptions,
        headers
      })

      // Handle 401 - token expired
      if (response.status === 401 && auth) {
        if (!isRefreshing) {
          isRefreshing = true
          const newToken = await refreshAccessToken()
          isRefreshing = false

          if (newToken) {
            onTokenRefreshed(newToken)
            headers['Authorization'] = `Bearer ${newToken}`
            response = await fetch(buildUrl(endpoint, params), {
              ...fetchOptions,
              headers
            })
          } else {
            // Redirect to login
            window.location.href = '/login'
            return { data: null, error: { message: 'Session expirée' }, success: false }
          }
        } else {
          // Wait for token refresh
          return new Promise((resolve) => {
            addRefreshSubscriber(async (token) => {
              headers['Authorization'] = `Bearer ${token}`
              const retryResponse = await fetch(buildUrl(endpoint, params), {
                ...fetchOptions,
                headers
              })
              const data = await retryResponse.json()
              resolve({ data, error: null, success: true })
            })
          })
        }
      }

      // Parse response
      const data = await response.json()

      if (!response.ok) {
        const apiError: ApiError = {
          message: data.message || 'Une erreur est survenue',
          status: response.status,
          errors: data.errors
        }
        error.value = apiError
        return { data: null, error: apiError, success: false }
      }

      return { data, error: null, success: true }
    } catch (err) {
      const apiError: ApiError = {
        message: err instanceof Error ? err.message : 'Erreur de connexion',
        status: 0
      }
      error.value = apiError
      return { data: null, error: apiError, success: false }
    } finally {
      loading.value = false
    }
  }

  // Convenience methods
  const get = <T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET', params })

  const post = <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'POST', body: JSON.stringify(body) })

  const put = <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PUT', body: JSON.stringify(body) })

  const patch = <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PATCH', body: JSON.stringify(body) })

  const del = <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' })

  return {
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    request,
    get,
    post,
    put,
    patch,
    del,
    setTokens,
    clearTokens,
    getAccessToken
  }
}

export type { ApiError, ApiResponse }
